import type { HeaderBannerProps } from '@/components/shared/HeaderBanner';
import type { CustomersLoveSectionProps } from '@/components/vets/CustomersLoveSection';
import type { FeaturesSectionProps } from '@/components/vets/FeaturesSection';
import type { GoodForWorldSectionProps } from '@/components/vets/GoodForWorldSection';
import type { GoodToKnowSectionProps } from '@/components/vets/GoodToKnowSection';
import type { HeroProps } from '@/components/vets/Hero';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/vets/vets-og.webp';
import burdenFreeIcon from '@/static/assets/vets/burden-free-icon.svg';
import burdenFreeIconMobile from '@/static/assets/vets/burden-free-icon-mobile.svg';
import seamlessClaimsIcon from '@/static/assets/vets/seamless-claims-icon.svg';
import seamlessClaimsIconMobile from '@/static/assets/vets/seamless-claims-icon-mobile.svg';
import noMiddlemanIcon from '@/static/assets/vets/no-middleman-icon.svg';
import noMiddlemanIconMobile from '@/static/assets/vets/no-middleman-icon-mobile.svg';
import accidentIllnessIcon from '@/static/assets/vets/accident-illness-icon.svg';
import accidentIllnessIconMobile from '@/static/assets/vets/accident-illness-icon-mobile.svg';
import addOnsIcon from '@/static/assets/vets/add-ons-icon.svg';
import addOnsIconMobile from '@/static/assets/vets/add-ons-icon-mobile.svg';
import routineCareIcon from '@/static/assets/vets/routine-care-icon.svg';
import routineCareIconMobile from '@/static/assets/vets/routine-care-icon-mobile.svg';
import logoFriendsOfAnimals from '@/static/assets/vets/logo-friends-of-animals.webp';
import logoSpca from '@/static/assets/vets/logo-spca.webp';
import logoMuddyPaws from '@/static/assets/vets/logo-muddy-paws.webp';
import type { BasePageConfig } from '../BasePageConfig';

const CTA_URL = `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/pet/us/vet/signup`;
const CTA_TEXT = 'CHECK IT OUT';

export interface Config extends BasePageConfig {
  headerBanner?: HeaderBannerProps;
  hero: HeroProps;
  featuresSection: FeaturesSectionProps;
  customersLoveSection: CustomersLoveSectionProps;
  coverageInfoSection: FeaturesSectionProps;
  goodForWorldSection: GoodForWorldSectionProps;
  goodToKnowSection: GoodToKnowSectionProps;
}

export const config: Config = {
  layout: { ...sharedConfig['en-US'].layout },
  seo: {
    title: 'We handle the insurance You handle the healing – Lemonade',
    metaTitle: 'We handle the insurance You handle the healing – Lemonade',
    description: 'Lemonade makes it easier to keep pets healthy, and your practice growing',
    ogImage: ogImage.src,
  },
  hero: {
    title: 'We handle the insurance\nYou handle the healing',
    titleMobile: 'We handle the\ninsurance\nYou handle the healing',
    subtitle: 'Lemonade makes it easier to keep pets healthy, and your practice growing',
    ctaText: CTA_TEXT,
    ctaUrl: CTA_URL,
  },
  featuresSection: {
    noPaddingTop: true,
    title: 'The ideal pet insurance for busy clinics',
    subtitle: 'No onboarding. No software. Just simple, seamless support.',
    cards: [
      {
        image: burdenFreeIcon,
        imageMobile: burdenFreeIconMobile,
        title: 'Burden-free',
        content: 'No forms, no portals, no hassle. We keep things friction-free so your team can focus on care.',
      },
      {
        image: seamlessClaimsIcon,
        imageMobile: seamlessClaimsIconMobile,
        title: 'Seamless claims',
        content: "Claims? Not your problem. We're known for the fastest claims experience that pet parents love",
      },
      {
        image: noMiddlemanIcon,
        imageMobile: noMiddlemanIconMobile,
        title: 'We handle it all',
        content: "Lemonade guides pet parents to their perfect plan, so you don't have to.",
      },
    ],
    ctaText: CTA_TEXT,
    ctaUrl: CTA_URL,
  },
  customersLoveSection: {
    title: 'Pet parents love Lemonade',
    subtitle: "Fast, reliable, award-winning service that's there when you need it most",
    leftCallout: {
      title: '4.9 stars',
      text: 'highest rated on\nthe app store',
    },
    rightCallout: {
      prefix: 'Claims settled',
      bold: 'in seconds!',
    },
  },
  coverageInfoSection: {
    title: 'When you recommend care, our coverage\nhelps pet parents afford it',
    subtitle: "Customers build coverage around their pet's needs without paying for extras",
    cards: [
      {
        image: accidentIllnessIcon,
        imageMobile: accidentIllnessIconMobile,
        title: 'Accident & Illness',
        content: 'Helps cover diagnostics, procedures, medications, and hospitalizations',
      },
      {
        image: addOnsIcon,
        imageMobile: addOnsIconMobile,
        title: 'Optional add-ons',
        content: 'Dental, visit fees, behavioral, and physical therapy',
      },
      {
        image: routineCareIcon,
        imageMobile: routineCareIconMobile,
        title: 'Routine care packages',
        content: 'Vaccines, annual checkups, spay, neuter, and other healthy life essentials',
      },
    ],
    ctaText: CTA_TEXT,
    ctaUrl: CTA_URL,
  },
  goodForWorldSection: {
    title: 'Good for vets, pets, and the world',
    subtitle:
      "Lemonade is a public benefit corporation, and here's something you'll love: with Lemonade, pet insurance does double duty. Policyholders pick a cause that matters to them, like animal welfare, and part of their premium goes toward funding nonprofits in that area.",
    logos: [
      { src: logoFriendsOfAnimals, alt: 'Friends of Animals' },
      { src: logoMuddyPaws, alt: 'Muddy Paws Rescue' },
      { src: logoSpca, alt: 'SPCA International' },
    ],
  },
  goodToKnowSection: {
    title: 'Good to know…',
    subtitle:
      'We built our insurance to be easy for pet parents and to fit your practice.\nZero setup, zero headaches, zero changes to how you work today.',
    faqs: [
      {
        question: 'How do I get my practice set up to work with Lemonade pet insurance?',
        answer:
          "There's no formal setup required on your end. Pet parents handle their own claims through the app by uploading your invoices and records. We'll take care of the insurance so you can focus on caring for pets!",
      },
      {
        question: 'How long does it take for my clients to get Lemonade coverage for their pets?',
        answer:
          'Pet parents can get a quote in about 90 seconds and coverage starts the day after purchase. Once the policy is active, accidents and preventative care are covered, while illnesses have a 14-day waiting period and orthopedic conditions require 30 days.',
      },
      {
        question: 'Do you work with all veterinary practices?',
        answer:
          "Yes, we work with any licensed veterinary practice in the US. There are no network restrictions or contracts required. If you're a licensed vet, you can treat Lemonade-covered pets without any additional steps or approvals.",
      },
      {
        question: "How do Lemonade's instant claims help my clients afford treatment?",
        answer:
          'More than half of our claims are settled instantly, sometimes with immediate confirmation treatment will be reimbursed. Knowing their claim is already approved significantly reduces financial stress.',
      },
      {
        question: 'How does working with Lemonade-covered pets benefit my practice?',
        answer:
          "With faster reimbursements, you'll see fewer payment plan requests. It's a win-win. Customers with insurance are more likely to approve recommended treatments and less likely to delay or skip care because of cost.",
      },
      {
        question: 'Do I need to pre-authorize treatments with Lemonade?',
        answer:
          'We offer pre-approval for procedures estimated at $3,500 or more. Your clients can proceed with any recommended treatment and submit claims afterward, Lemonade walks them through the process either way.',
      },
      {
        question: 'What happens if they have questions about Lemonade during their visit?',
        answer:
          "You don't need to become a policy expert, just focus on providing excellent veterinary care while we handle the insurance details. We provide you with helpful resources, but Lemonade is so easy to use that you can just direct your customer to our app.",
      },
      {
        question: 'What types of treatments are covered?',
        answer:
          'Lemonade covers accidents and illnesses including diagnostics, surgery, medications, and emergency care. Add-on coverage includes visit fees, physical therapy, dental care, and behavioral treatments. Preventive care like exams and vaccines are available as an optional wellness add-on.',
      },
      {
        question: 'Does Lemonade cover pre-existing conditions?',
        answer:
          "No, we don't cover pre-existing conditions. Any health issue your patient showed signs of before their waiting period ends won't be covered. However, we do cover some previously treated conditions once fully cured, like kennel cough.",
      },
    ],
  },
};
